// AppVersionBadge (2026-10-01): discreet version indicator for menu headers.
// Shows `v<packageVersion>` in a muted color next to the Vecindus wordmark.
// The version is baked at build time via the vite define __APP_VERSION__
// (package.json version). Clicking copies `v<version>` with a tiny toast.
// Diagnostic escape hatch (2026-10-01, TH1): a second click within 3s opens
// a small 'Diagnóstico' modal showing sessionStorage['vecindus_sel_trace']
// (pretty-printed JSON, scrollable) so the trace can be sent from the
// installed PWA without devtools. Discreet: same muted styling, no other UI.
import React, { useRef, useState } from 'react';

declare const __APP_VERSION__: string;

interface AppVersionBadgeProps {
  className?: string;
}

export const APP_VERSION_LABEL = `v${__APP_VERSION__}`;

const SEL_TRACE_KEY = 'vecindus_sel_trace';
const DOUBLE_CLICK_WINDOW_MS = 3000;

function readSelTrace(): unknown[] {
  try {
    const raw = sessionStorage.getItem(SEL_TRACE_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

async function readSwCacheName(): Promise<string | null> {
  try {
    if (typeof caches === 'undefined') return null;
    const keys = await caches.keys();
    return keys.length ? keys[0] : null;
  } catch {
    return null;
  }
}

export const AppVersionBadge: React.FC<AppVersionBadgeProps> = ({ className = '' }) => {
  const [copied, setCopied] = useState(false);
  const [diagOpen, setDiagOpen] = useState(false);
  const lastClickAt = useRef(0);

  const handleClick = async () => {
    const now = Date.now();
    const isSecondClick = now - lastClickAt.current <= DOUBLE_CLICK_WINDOW_MS;
    lastClickAt.current = now;
    if (isSecondClick) {
      lastClickAt.current = 0;
      setDiagOpen(true);
      return;
    }
    try {
      await navigator.clipboard.writeText(`v${__APP_VERSION__}`);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch { /* clipboard unavailable — badge stays read-only */ }
  };

  return (
    <span className={`relative inline-flex items-center ${className}`}>
      <button
        type="button"
        onClick={handleClick}
        title={copied ? '¡Versión copiada!' : 'Copiar versión'}
        className="rounded px-1 text-[10px] font-medium leading-none text-[var(--v-text-secondary)] opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-1 focus:ring-[var(--v-teal-500)]"
      >
        {copied ? 'Versión copiada' : APP_VERSION_LABEL}
      </button>
      {diagOpen && (
        <DiagModal onClose={() => setDiagOpen(false)} />
      )}
    </span>
  );
};

const DiagModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [trace] = useState<unknown[]>(() => readSelTrace());
  const [cacheName, setCacheName] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  React.useEffect(() => {
    void readSwCacheName().then(setCacheName);
  }, []);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(trace));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch { /* clipboard unavailable */ }
  };

  const pretty = trace.length
    ? JSON.stringify(trace, null, 2)
    : null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
      role="dialog"
      aria-label="Diagnóstico"
    >
      <div
        className="flex max-h-[70vh] w-full max-w-sm flex-col rounded-lg border border-[var(--v-border)] bg-[var(--v-surface)] shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-[var(--v-border)] px-3 py-2">
          <div className="text-xs font-semibold text-[var(--v-text-primary)]">Diagnóstico</div>
          <div className="mt-0.5 text-[10px] text-[var(--v-text-secondary)]">
            {APP_VERSION_LABEL}
            {cacheName ? ` · caché: ${cacheName}` : ''}
            {' · '}
            {navigator.onLine ? 'con conexión' : 'sin conexión'}
          </div>
        </div>
        <pre className="flex-1 overflow-auto whitespace-pre-wrap break-words px-3 py-2 text-[10px] leading-snug text-[var(--v-text-secondary)]" style={{ maxHeight: '40vh' }}>
          {pretty ?? 'Sin trazas registradas'}
        </pre>
        <div className="flex justify-end gap-2 border-t border-[var(--v-border)] px-3 py-2">
          <button
            type="button"
            onClick={handleCopy}
            className="rounded px-2 py-1 text-[11px] font-medium text-[var(--v-teal-500)]"
          >
            {copied ? 'Copiado' : 'Copiar'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="rounded px-2 py-1 text-[11px] font-medium text-[var(--v-text-secondary)]"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};
